/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Button } from '@cooltrax/jellyfish-ui'
import { MdReply, MdHome, MdRefresh, MdErrorOutline } from 'react-icons/md'

interface ErrorComponentProps {
  error?: Error
  resetErrorBoundary?: () => void
}

const ErrorComponent: React.FC<ErrorComponentProps> = ({ error, resetErrorBoundary }) => {
  const [isDarkMode, setIsDarkMode] = useState(false)

  useEffect(() => {
    // Check for theme from HTML data attribute (daisyUI theme)
    const checkTheme = () => {
      const htmlElement = document.documentElement
      const theme = htmlElement.getAttribute('data-theme')
      const isDark =
        theme?.includes('dark') ||
        theme === 'night' ||
        theme === 'halloween' ||
        theme === 'forest' ||
        theme === 'black' ||
        theme === 'luxury' ||
        theme === 'dracula' ||
        theme === 'synthwave' ||
        theme === 'cyberpunk' ||
        theme === 'dim' ||
        theme === 'sunset' ||
        theme === 'abyss'

      // Fallback to system preference if no theme is set
      if (!theme) {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
        setIsDarkMode(prefersDark)
      } else {
        setIsDarkMode(isDark || false)
      }
    }

    checkTheme()

    // Listen for theme changes
    const observer = new MutationObserver(checkTheme)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    })

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaQuery.addEventListener('change', checkTheme)

    return () => {
      observer.disconnect()
      mediaQuery.removeEventListener('change', checkTheme)
    }
  }, [])

  const handleReload = () => {
    window.location.reload()
  }

  const handleGoHome = () => {
    window.location.href = '/'
  }

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    // Hide the image if it fails to load
    e.currentTarget.style.display = 'none'
  }

  return (
    <div className="min-h-screen bg-base-100 flex items-center justify-center p-4">
      <div className="card bg-base-100 shadow-xl max-w-md w-full">
        <div className="card-body text-center">
          {/* Cooltrax Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={isDarkMode ? '/logo-dark.png' : '/logo-light.png'}
              alt="Cooltrax"
              className="h-12 w-auto"
              onError={handleImageError}
            />
          </div>

          {/* Error Icon */}
          <div className="flex justify-center mb-4">
            <MdErrorOutline className="text-6xl text-error" />
          </div>

          {/* Error Title */}
          <h1 className="card-title text-2xl text-error justify-center mb-2">Something went wrong</h1>

          {/* Error Description */}
          <p className="text-base-content/70 mb-6">
            We encountered an unexpected error. Please try one of the options below to continue.
          </p>

          {/* Error Details (Collapsible) */}
          {error && (
            <div className="collapse collapse-arrow bg-base-200 mb-6">
              <input type="checkbox" />
              <div className="collapse-title text-sm font-medium">View error details</div>
              <div className="collapse-content">
                <div className="bg-base-300 p-3 rounded-lg">
                  <p className="text-error text-xs font-mono break-all mb-2">{error.message}</p>
                  {error.stack && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-base-content/50 mb-2">Stack trace</summary>
                      <pre className="text-base-content/50 overflow-auto whitespace-pre-wrap">{error.stack}</pre>
                    </details>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="card-actions justify-center">
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <Button onClick={handleReload} color="primary" className="btn-md">
                <MdRefresh className="w-4 h-4 mr-2" />
                Reload Page
              </Button>

              <button onClick={handleGoHome} className="btn btn-outline btn-md">
                <MdHome className="w-4 h-4 mr-2" />
                Go to Home
              </button>

              {resetErrorBoundary && (
                <button onClick={resetErrorBoundary} className="btn btn-ghost btn-md">
                  <MdReply className="w-4 h-4 mr-2" />
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ErrorComponent
